import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { MapListing } from '../models/map-listing.model';
import { LegInfo, RouteSummary } from '../models/route-summary.model';
import { StartPoint } from '../models/start-point.model';
import { track } from './analytics';
import { ListingsService } from './listings.service';
import { SessionTripService } from './session-trip.service';
import { TripsService } from './trips.service';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// Bounding box handed from the server for a `?regija=<slug>` deep-link, used to
// frame the map on that region instead of the whole country. null when absent.
export interface RegionBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

const SYNC_DEBOUNCE_MS = 500;

@Injectable({ providedIn: 'root' })
export class PlannerStore {
  private readonly listingsService = inject(ListingsService);
  private readonly sessionTripService = inject(SessionTripService);
  private readonly tripsService = inject(TripsService);

  readonly stops = signal<number[]>([]);
  // Optional root starting point (city/village), null when unset.
  readonly startPoint = signal<StartPoint | null>(null);
  // Guid string once the plan is saved to DB, null while it lives in session only.
  readonly tripId = signal<string | null>(null);
  readonly tripName = signal<string | null>(null);
  readonly isLoggedIn = signal<boolean>(false);
  readonly saveState = signal<SaveState>('idle');
  readonly routeSummary = signal<RouteSummary | null>(null);
  // Region deep-link bounds from ?regija=<slug>; null = fit the whole country.
  readonly regionBounds = signal<RegionBounds | null>(null);

  private readonly listingsById = computed(() => {
    const map = new Map<number, MapListing>();
    // Resolve from the full unfiltered set so filtering the map never removes
    // stops from the plan/sidebar.
    for (const l of this.listingsService.allListings()) map.set(l.id, l);
    return map;
  });

  readonly stopListings = computed<MapListing[]>(() => {
    const byId = this.listingsById();
    const out: MapListing[] = [];
    for (const id of this.stops()) {
      const l = byId.get(id);
      if (l) out.push(l);
    }
    return out;
  });

  readonly hasStop = (id: number) => this.stops().includes(id);

  // Signature of the ordered points the route runs through: the optional start
  // point, then each stop. Computed here so the store and MapView agree on it.
  readonly waypointsKey = computed(() => {
    const start = this.startPoint();
    const parts = start ? [`s:${start.lat},${start.lng}`] : [];
    for (const s of this.stopListings()) parts.push(String(s.id));
    return parts.join('|');
  });

  // The leg that leads INTO stops[i], or null when there is none. Aligned 1:1
  // with stopListings(). Google returns one leg per gap between waypoints, so a
  // start point shifts the whole array by one.
  //
  // The key check drops every leg while a plan edit is still waiting on a fresh
  // route (250ms debounce plus the network call). Without it a reorder - which
  // leaves the leg count unchanged - would show the old distances against the
  // new order.
  readonly stopLegs = computed<(LegInfo | null)[]>(() => {
    const stops = this.stopListings();
    const summary = this.routeSummary();
    if (!summary || summary.waypointsKey !== this.waypointsKey()) {
      return stops.map(() => null);
    }
    const offset = this.startPoint() ? 0 : -1;
    return stops.map((_, i) => summary.legs[i + offset] ?? null);
  });

  // Suppresses the sync effect while we hydrate from the backend so we don't
  // immediately PATCH the state we just fetched.
  private suppressSync = true;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      // Track all synced signals so the effect fires on any change.
      const stops = this.stops();
      const name = this.tripName();
      const start = this.startPoint();
      if (this.suppressSync) return;
      untracked(() => this.scheduleSync(stops, name, start));
    });
  }

  addStop(id: number): void {
    if (this.stops().includes(id)) return;
    this.stops.update((s) => [...s, id]);
    track('planner_add_stop', { listing_id: id });
  }

  removeStop(id: number): void {
    if (!this.stops().includes(id)) return;
    this.stops.update((s) => s.filter((x) => x !== id));
    track('planner_remove_stop', { listing_id: id });
  }

  moveStop(id: number, dir: 'up' | 'down'): void {
    this.stops.update((s) => {
      const i = s.indexOf(id);
      if (i < 0) return s;
      const j = dir === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= s.length) return s;
      const copy = s.slice();
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  renameTrip(name: string): void {
    this.tripName.set(name);
  }

  setStartPoint(start: StartPoint): void {
    this.startPoint.set(start);
  }

  clearStartPoint(): void {
    this.startPoint.set(null);
  }

  setRouteSummary(summary: RouteSummary | null): void {
    this.routeSummary.set(summary);
    if (summary) {
      track('planner_route_computed', {
        stops: this.stops().length,
        distance_m: summary.distanceMeters,
        duration_s: summary.durationSeconds,
      });
    }
  }

  // Called from bootstrap after meta tags are read and listings fetched.
  // Populates state without triggering a sync-back to the server.
  async hydrate(opts: {
    tripId: string | null;
    isLoggedIn: boolean;
    regionBounds?: RegionBounds | null;
  }): Promise<void> {
    this.isLoggedIn.set(opts.isLoggedIn);
    this.tripId.set(opts.tripId);
    this.regionBounds.set(opts.regionBounds ?? null);
    track('planner_session_start', {
      logged_in: opts.isLoggedIn,
      opened_saved: opts.tripId != null,
    });

    try {
      if (opts.tripId) {
        const trip = await this.tripsService.fetch(opts.tripId);
        this.stops.set(trip.stop_ids);
        this.tripName.set(trip.name);
        this.startPoint.set(trip.start);
      } else {
        const state = await this.sessionTripService.fetch();
        this.stops.set(state.stop_ids);
        this.tripName.set(state.name);
        this.startPoint.set(state.start);
      }
    } finally {
      // Even on fetch failure, unmute sync so subsequent user changes still
      // reach the backend (they'll start from whatever local state we have).
      this.suppressSync = false;
    }
  }

  // "Save plan" click handler.
  // - Anon: navigate to /sign-in with save_planner=1. The `new` action stores
  //   the intent in the session hash, so it survives navigating from sign-in
  //   to signup — a user who doesn't have an account can register and their
  //   plan still gets migrated by after_sign_in_path_for on signup success.
  // - Logged-in: POST /api/v1/trips (backend reads current session), then
  //   replaceState to /planer/:guid so subsequent PATCHes go to trips/:id.
  async saveTrip(): Promise<void> {
    if (!this.isLoggedIn()) {
      track('planner_save_intent_anon');
      window.location.href = '/sign-in?save_planner=1';
      return;
    }

    this.saveState.set('saving');
    try {
      // Flush any pending session sync so backend sees the latest stops.
      await this.flushPendingSync();

      const trip = await this.tripsService.createFromSession();
      this.tripId.set(trip.id);
      this.tripName.set(trip.name);
      this.stops.set(trip.stop_ids);
      this.startPoint.set(trip.start);
      window.history.replaceState(null, '', `/planer/${trip.id}`);
      this.saveState.set('saved');
      track('planner_save_trip', { stops: trip.stop_ids.length });
    } catch (err) {
      console.error('[planner] saveTrip failed', err);
      this.saveState.set('error');
    }
  }

  private scheduleSync(stopIds: number[], name: string | null, start: StartPoint | null): void {
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      this.syncNow(stopIds, name, start).catch((err) => {
        console.error('[planner] sync failed', err);
        this.saveState.set('error');
      });
    }, SYNC_DEBOUNCE_MS);
  }

  private async flushPendingSync(): Promise<void> {
    if (!this.syncTimer) return;
    clearTimeout(this.syncTimer);
    this.syncTimer = null;
    await this.syncNow(this.stops(), this.tripName(), this.startPoint());
  }

  private async syncNow(
    stopIds: number[],
    name: string | null,
    start: StartPoint | null
  ): Promise<void> {
    const guid = this.tripId();
    if (guid) {
      await this.tripsService.update(guid, { stop_ids: stopIds, name, start });
    } else {
      await this.sessionTripService.update({ stop_ids: stopIds, name, start });
    }
  }
}
